import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth headers for API requests
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to every request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors globally AND auto-unwrap paginated responses
apiClient.interceptors.response.use(
  (response) => {
    // Auto-unwrap paginated responses: {data: [...], total, page, limit} → [...]
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data) && Array.isArray(response.data.data)) {
      const { data: arrayData, ...paginationMeta } = response.data;
      response.pagination = paginationMeta;
      response.data = arrayData;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Utility to safely extract array data from API response
 * Handles both array responses and paginated {data: [...]} responses
 * @param {any} responseData - The response.data from axios
 * @returns {Array} - Always returns an array
 */
export const extractArrayData = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (responseData && Array.isArray(responseData.data)) {
    return responseData.data;
  }
  return [];
};

/**
 * Utility to get pagination info from response
 * @param {any} responseData - The response.data from axios
 * @returns {Object} - Pagination info {total, page, limit, data}
 */
export const getPaginationInfo = (responseData) => {
  if (Array.isArray(responseData)) {
    return { data: responseData, total: responseData.length, page: 1, limit: responseData.length };
  }
  if (responseData && typeof responseData === 'object') {
    return {
      data: responseData.data || [],
      total: responseData.total || 0,
      page: responseData.page || 1,
      limit: responseData.limit || 25,
      stats: responseData.stats || null
    };
  }
  return { data: [], total: 0, page: 1, limit: 25 };
};

// Helper functions for common operations
export const apiGet = async (url) => {
  const response = await apiClient.get(url);
  return response.data;
};

export const apiPost = async (url, data) => {
  const response = await apiClient.post(url, data);
  return response.data;
};

export const apiPut = async (url, data) => {
  const response = await apiClient.put(url, data);
  return response.data;
};

export const apiDelete = async (url) => {
  const response = await apiClient.delete(url);
  return response.data;
};

export default apiClient;
